import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Save, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsManager = () => {
  const [heroImageUrl, setHeroImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHeroImage();
  }, []);

  const fetchHeroImage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/settings/hero-image`);
      if (response.data.success) {
        setHeroImageUrl(response.data.hero_image_url);
      }
    } catch (error) {
      console.error('Error fetching hero image:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Set a default hero image even if fetch fails
      setHeroImageUrl('https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg');
      toast({
        title: 'Warning',
        description: 'Using default hero image. Could not load custom settings.',
        variant: 'default'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!heroImageUrl.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a valid image URL',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSaving(true);
      const response = await axios.put(
        `${BACKEND_URL}/api/settings/hero-image`,
        { hero_image_url: heroImageUrl }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'Hero image updated successfully. Refresh the home page to see changes.'
        });
      }
    } catch (error) {
      console.error('Error updating hero image:', error);
      toast({
        title: 'Error',
        description: 'Failed to update hero image',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-6 h-6" />
            Hero Image Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="hero-image-url">Hero Image URL</Label>
            <Input
              id="hero-image-url"
              type="url"
              value={heroImageUrl}
              onChange={(e) => setHeroImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full"
            />
            <p className="text-sm text-gray-500">
              Enter the URL of the image you want to display on the homepage hero section.
              Recommended size: 1920x1080px
            </p>
          </div>

          {heroImageUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="border rounded-lg overflow-hidden">
                <img
                  src={heroImageUrl}
                  alt="Hero preview"
                  className="w-full h-64 object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/1920x1080?text=Invalid+Image+URL';
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-4">
            <div className="text-sm text-gray-600">
              <p className="font-medium mb-1">Free Image Sources:</p>
              <ul className="list-disc list-inside space-y-1">
                <li><a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Pexels.com</a></li>
                <li><a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Unsplash.com</a></li>
                <li><a href="https://pixabay.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Pixabay.com</a></li>
              </ul>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-gray-700">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold mb-2">How to update the hero image:</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>Find a high-quality image from free image sources (Pexels, Unsplash, etc.)</li>
              <li>Copy the direct image URL (right-click → Copy Image Address)</li>
              <li>Paste the URL in the "Hero Image URL" field above</li>
              <li>Check the preview to ensure the image loads correctly</li>
              <li>Click "Save Changes" to update</li>
              <li>Refresh your homepage to see the new hero image</li>
            </ol>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold mb-2">⚠️ Important Notes:</h4>
            <ul className="list-disc list-inside space-y-1">
              <li>Use high-resolution images (at least 1920x1080px) for best quality</li>
              <li>Ensure the image URL is publicly accessible (no login required)</li>
              <li>Use HTTPS URLs for security</li>
              <li>Choose images that match your brand and service theme</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsManager;
